import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  FormControlLabel,
  Switch,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import {
  Send as SendIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Telegram as TelegramIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as SmartToyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarTodayIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from '../../config/axios';
import { io, Socket } from 'socket.io-client';
import { getApiUrl, getWebSocketUrl } from '../../config/environment';
import BotToggleButton from '../../components/BotToggleButton';
import QuickMessagesButton from '../../components/QuickMessagesButton';
import AttendantAssignment from '../../components/AttendantAssignment';
import { logger } from '../../utils/logger';

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  custom_fields?: {
    telegram_id?: string;
    telegram_username?: string;
  };
}

interface Conversation {
  id: string;
  contact_id: string;
  channel_id: string;
  external_id?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  contact?: Contact;
  channel?: {
    id: string;
    name: string;
    type: string;
    status: string;
  };
}

interface Message {
  id: string;
  conversation_id: string;
  external_id?: string;
  content: string;
  direction: 'inbound' | 'outbound';
  sent_at: string;
  status: string;
  content_type?: string;
  metadata?: Record<string, any>;
  attachments?: Array<{
    type: string;
    url: string;
    file_id?: string;
    width?: number;
    height?: number;
    file_name?: string;
    mime_type?: string;
    duration?: number;
    title?: string;
    performer?: string;
    emoji?: string;
  }>;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  status: string;
}

const API_BASE_URL = getApiUrl();
const WS_URL = getWebSocketUrl();

const Telegram: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const [showArchived, setShowArchived] = useState(false);
  const selectedConversationRef = useRef<string>('');
  
  // Atualizar ref quando selectedConversation mudar
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState<string | null>(null);
  const [deletingContact, setDeletingContact] = useState<string | null>(null);
  const [openLeadDetailsDialog, setOpenLeadDetailsDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [stage4Leads, setStage4Leads] = useState<Set<string>>(new Set());
  const [allLeadsStages, setAllLeadsStages] = useState<Map<string, number>>(new Map());
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [crmStages, setCrmStages] = useState<Array<{ id: string; name: string; stage_order: number; color: string }>>([]);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState<{ el: HTMLElement; messageId: string } | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [conversationsPage, setConversationsPage] = useState(1);
  const conversationsPageRef = useRef(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sendingToAgenda, setSendingToAgenda] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'info' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Abrir conversa específica se contactId estiver na URL
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (contactId) {
      // Se há um contactId na URL, buscar conversas diretamente desse contato
      const fetchConversationForContact = async () => {
        try {
          logger.debug('Buscando conversa para contactId');
          
          // Buscar conversas específicas desse contato
          const contactConversationResponse = await axios.get(
            `${API_BASE_URL}/conversations?channel=telegram&contact_id=${contactId}&limit=10`
          );
          
          if (contactConversationResponse.data.success) {
            const contactConversations = contactConversationResponse.data.data || [];
            
            if (contactConversations.length > 0) {
              // Pegar a conversa mais recente desse contato
              const mostRecentConversation = contactConversations.sort((a: any, b: any) => {
                const dateA = new Date(a.last_message_at || a.updated_at || a.created_at || 0);
                const dateB = new Date(b.last_message_at || b.updated_at || b.created_at || 0);
                return dateB.getTime() - dateA.getTime();
              })[0];
              
              logger.debug('Conversa encontrada');
              setSelectedConversation(mostRecentConversation.id);
              
              // IMPORTANTE: Carregar TODAS as mensagens desta conversa quando há contactId na URL
              // Isso garante que mensagens antigas sejam carregadas mesmo que não estejam entre as últimas 50
              await fetchMessages(mostRecentConversation.id, true); // true = loadAll
              
              // Buscar também as outras conversas do Telegram para exibir na lista
              const allConversationsResponse = await axios.get(
                `${API_BASE_URL}/conversations?channel=telegram&limit=50&page=1`
              );
              
              if (allConversationsResponse.data.success) {
                const allConversations = allConversationsResponse.data.data || [];
                
                // Garantir que a conversa selecionada esteja na lista
                const conversationExists = allConversations.some((c: any) => c.id === mostRecentConversation.id);
                if (!conversationExists) {
                  allConversations.unshift(mostRecentConversation);
                }
                
                setConversations(allConversations);
                await checkAllLeadsStages(allConversations);
              }
            } else {
              console.warn('⚠️ Nenhuma conversa encontrada para este contato');
              // Buscar todas as conversas normalmente
              const response = await axios.get(`${API_BASE_URL}/conversations?channel=telegram&limit=50&page=1`);
              if (response.data.success) {
                setConversations(response.data.data || []);
                await checkAllLeadsStages(response.data.data || []);
              }
            }
            
            // Limpar o parâmetro da URL após abrir a conversa
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('contactId');
            window.history.replaceState({}, '', newUrl.toString());
          }
        } catch (error) {
          console.error('❌ Erro ao buscar conversas do Telegram:', error);
          // Em caso de erro, buscar todas as conversas normalmente
          try {
            const response = await axios.get(`${API_BASE_URL}/conversations?channel=telegram&limit=50&page=1`);
            if (response.data.success) {
              setConversations(response.data.data || []);
              await checkAllLeadsStages(response.data.data || []);
            }
          } catch (err) {
            logger.error('Erro ao buscar conversas após falha:', err);
          }
        }
      };
      
      fetchConversationForContact();
    }
  }, [searchParams]); // Removido checkAllLeadsStages das dependências para evitar erro de ordem de declaração

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (name: string) => {
    const colors = [
      '#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5',
      '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
      '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
      '#ff5722', '#795548', '#607d8b'
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Função para atualizar avatar do contato
  const updateContactAvatar = async (contactId: string) => {
    try {
      setUpdatingAvatar(contactId);
      const response = await axios.post(`${API_BASE_URL}/contacts/${contactId}/update-avatar`);
      
      if (response.data.success) {
        // Atualizar a lista de conversas para refletir a mudança
        await fetchConversations();
        
        // Se a conversa estiver selecionada, atualizar as mensagens também
        if (selectedConversation) {
          await fetchMessages(selectedConversation);
        }
      }
    } catch (error) {
      logger.error('Error updating avatar:', error);
      setError('Erro ao atualizar foto de perfil');
    } finally {
      setUpdatingAvatar(null);
    }
  };

  // Função para excluir contato
  const deleteContact = async (contactId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setDeletingContact(contactId);
      const response = await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
      
      if (response.data.success) {
        // Remover a conversa da lista
        setConversations(prev => prev.filter(conv => conv.contact_id !== contactId));
        
        // Se a conversa excluída estava selecionada, limpar a seleção
        if (selectedConversation && conversations.find(c => c.id === selectedConversation)?.contact_id === contactId) {
          setSelectedConversation('');
          setMessages([]);
        }
        
        setError('');
      }
    } catch (error) {
      logger.error('Error deleting contact:', error);
      setError('Erro ao excluir contato');
    } finally {
      setDeletingContact(null);
    }
  };

  // Função para scroll automático para a última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Função para normalizar URL de attachment (garantir que seja absoluta se necessário)
  const normalizeAttachmentUrl = (url: string | undefined): string => {
    if (!url) return '';
    // Se já é uma URL completa (http/https), retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Se for URL relativa, construir URL completa usando a base da API
    const apiBase = API_BASE_URL.replace('/api/v1', '');
    return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/${url}`;
  };

  // Atualizar ref quando conversationsPage mudar
  useEffect(() => {
    conversationsPageRef.current = conversationsPage;
  }, [conversationsPage, showArchived]);

  // Função para buscar conversas (memoizada)
  const fetchConversations = useCallback(async (reset: boolean = true) => {
    try {
      setError('');
      
      if (reset) {
        setConversationsPage(1);
        conversationsPageRef.current = 1;
        setHasMoreConversations(true);
      }
      
      // Usar ref para garantir que sempre use o valor mais recente
      const page = reset ? 1 : conversationsPageRef.current;
      const limit = 50; // Reduzido de 200 para 50
      
      // Buscar conversas do Telegram com paginação e ordenação por last_message_at
      const statusFilter = showArchived ? 'archived' : 'open';
      const conversationsResponse = await axios.get(`${API_BASE_URL}/conversations?channel=telegram&limit=${limit}&page=${page}&sortBy=last_message_at&sortOrder=desc&status=${statusFilter}`, {
        timeout: 30000 // 30 segundos de timeout
      });
      
      if (conversationsResponse.data.success) {
        const newConversations: Conversation[] = conversationsResponse.data.data || [];
        const pagination = conversationsResponse.data.pagination;
        
        // Verificar se há mais conversas para carregar
        const hasMore = pagination ? page < pagination.totalPages : newConversations.length === limit;
        setHasMoreConversations(hasMore);
        
        // Ordenar conversas por last_message_at (mais recentes primeiro)
        const sortedNewConversations = newConversations.sort((a: any, b: any) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        if (reset) {
          setConversations(sortedNewConversations);
          // Verificar estágios de todos os leads
          await checkAllLeadsStages(sortedNewConversations);
        } else {
          // Adicionar novas conversas às existentes, evitando duplicatas
          setConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = sortedNewConversations.filter(c => !existingIds.has(c.id));
            const combined = [...prev, ...uniqueNew].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            return combined;
          });
        }
        
        // Atualizar página para a próxima
        const nextPage = page + 1;
        setConversationsPage(nextPage);
        conversationsPageRef.current = nextPage;
      } else {
        // Fallback para resposta sem paginação
        const conversationsData: Conversation[] = conversationsResponse.data.data || [];
        const sortedConversations = conversationsData.sort((a: any, b: any) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        if (reset) {
          setConversations(sortedConversations);
          await checkAllLeadsStages(sortedConversations);
        } else {
          setConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = sortedConversations.filter(c => !existingIds.has(c.id));
            return [...prev, ...uniqueNew].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          });
        }
        
        setHasMoreConversations(sortedConversations.length === limit);
        const nextPage = page + 1;
        setConversationsPage(nextPage);
        conversationsPageRef.current = nextPage;
      }
    } catch (err) {
      logger.error('Erro ao buscar conversas:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar conversas');
      }
    } finally {
      setLoading(false);
      setLoadingMoreConversations(false);
    }
  }, []);

  // Função para arquivar conversa
  const handleArchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'archived'
      });
      
      // Atualizar estado local
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // Se a conversa arquivada estava selecionada, limpar seleção
      if (selectedConversation === conversationId) {
        setSelectedConversation('');
        setMessages([]);
      }
    } catch (err) {
      logger.error('Erro ao arquivar conversa:', err);
      setError('Erro ao arquivar conversa');
    }
  }, [selectedConversation]);

  // Função para desarquivar conversa
  const handleUnarchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'open'
      });
      
      // Buscar conversa atualizada do backend
      const response = await axios.get(`${API_BASE_URL}/conversations/${conversationId}`);
      if (response.data.success) {
        const updatedConversation = response.data.data;
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversationId);
          if (exists) {
            return prev.map(c => c.id === conversationId ? updatedConversation : c);
          } else {
            return [...prev, updatedConversation].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          }
        });
      }
    } catch (err) {
      logger.error('Erro ao desarquivar conversa:', err);
      setError('Erro ao desarquivar conversa');
    }
  }, []);

  // Função para carregar mais conversas
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConversations || !hasMoreConversations) return;
    
    setLoadingMoreConversations(true);
    await fetchConversations(false);
  }, [loadingMoreConversations, hasMoreConversations, fetchConversations]);

  // Função para marcar conversa como lida
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/conversations/${conversationId}/mark-as-read`);
      
      // Atualizar a lista de conversas para refletir a mudança
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
    } catch (err) {
      logger.error('Erro ao marcar conversa como lida:', err);
      // Não mostrar erro para o usuário, pois não é crítico
    }
  }, []);

  // Log das mensagens quando mudarem
  useEffect(() => {
    console.log(`🎨 Mensagens no estado: ${messages.length}`);
    messages.forEach((message, index) => {
      console.log(`📝 Mensagem ${index + 1}/${messages.length}: ${message.content?.substring(0, 30) || '(sem conteúdo)'}...`);
    });
  }, [messages]);

  // Função para buscar mensagens de uma conversa
  const fetchMessages = useCallback(async (conversationId: string, loadAll: boolean = false) => {
    try {
      logger.debug(`Buscando mensagens para conversa: ${conversationId}, loadAll: ${loadAll}`);
      
      // Quando loadAll é true (ex: quando há contactId na URL), carregar TODAS as mensagens
      // Caso contrário, carregar apenas as últimas 50 para performance
      const limit = loadAll ? 10000 : 1000; // 10000 é um número alto suficiente para pegar todas as mensagens
      
      const response = await axios.get(`${API_BASE_URL}/messages/conversation/${conversationId}?limit=${limit}&sortOrder=asc`, {
        timeout: 30000 // 30 segundos de timeout
      });
      
      logger.apiResponse('fetchMessages', response.data);
      let messagesData: Message[] = response.data.data || [];
      logger.debug(`Total de mensagens recebidas: ${messagesData.length}`);
      
      // Normalizar attachments para garantir que seja sempre um array
      messagesData = messagesData.map(msg => {
        if (msg.attachments) {
          if (Array.isArray(msg.attachments)) {
            return { ...msg, attachments: msg.attachments };
          } else if (typeof msg.attachments === 'string') {
            try {
              return { ...msg, attachments: JSON.parse(msg.attachments) };
            } catch (e) {
              logger.error(`Erro ao parsear attachments da mensagem:`, e);
              return { ...msg, attachments: [] };
            }
          } else {
            return { ...msg, attachments: [] };
          }
        } else {
          return { ...msg, attachments: [] };
        }
      });
      
      // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
      const sortedMessages = messagesData.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
      console.log(`📋 Mensagens ordenadas: ${sortedMessages.length}`);
      
      setMessages(sortedMessages);
      console.log(`🎯 Mensagens definidas no estado: ${sortedMessages.length}`);
      
      // Scroll para a última mensagem após um pequeno delay
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (err) {
      logger.error('Erro ao buscar mensagens:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar mensagens');
      }
    }
  }, []);

  // Função para enviar mensagem (sem estado, apenas ref)
  const handleSendMessage = useCallback(async () => {
    const currentMessage = messageInputRef.current?.value?.trim() || '';
    const currentFile = selectedFile;
    const currentConversation = selectedConversation;
    const isSending = sendingMessage;
    
    if ((!currentMessage && !currentFile) || !currentConversation || isSending) return;

    // Se houver arquivo selecionado, enviar como mídia
    if (currentFile) {
      await handleSendMedia(currentFile);
      return;
    }

    // Caso contrário, enviar mensagem de texto normal
    setSendingMessage(true);
    try {
      // Buscar messageId para responder (pode estar em external_id ou metadata)
      const replyMessageId = replyingToMessage?.external_id || 
                             replyingToMessage?.metadata?.messageId || 
                             replyingToMessage?.metadata?.external_id;
      
      const response = await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: currentConversation,
        content: currentMessage,
        content_type: 'text',
        messageId: replyMessageId || undefined
      });

      // Limpar campo via ref (sem estado)
      if (messageInputRef.current) {
        messageInputRef.current.value = '';
      }
      
      // Limpar mensagem sendo respondida
      setReplyingToMessage(null);

      // Adicionar mensagem imediatamente ao estado (otimistic update)
      if (response.data.success && response.data.data) {
        const newMessage = response.data.data;
        // Garantir que attachments seja sempre um array
        const messageWithAttachments = {
          ...newMessage,
          attachments: Array.isArray(newMessage.attachments) 
            ? newMessage.attachments 
            : (typeof newMessage.attachments === 'string' 
                ? (() => {
                    try {
                      return JSON.parse(newMessage.attachments);
                    } catch {
                      return [];
                    }
                  })()
                : [])
        };

        setMessages(prev => {
          // Verificar se a mensagem já existe (evitar duplicatas do WebSocket)
          const exists = prev.some(msg => msg.id === messageWithAttachments.id);
          if (exists) {
            return prev;
          }
          const newMessages = [...prev, messageWithAttachments];
          // Ordenar mensagens por data de envio (mais antigas primeiro)
          return newMessages.sort((a, b) => 
            new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
          );
        });

        // Atualizar conversa na lista
        setConversations(prev => 
          prev.map(conv => 
            conv.id === currentConversation
              ? {
                  ...conv,
                  last_message_at: messageWithAttachments.sent_at || messageWithAttachments.created_at,
                  last_message: currentMessage
                }
              : conv
          ).sort((a, b) => {
            const dateA = new Date(a.last_message_at || a.updated_at || 0);
            const dateB = new Date(b.last_message_at || b.updated_at || 0);
            return dateB.getTime() - dateA.getTime();
          })
        );

        // Scroll para o final após adicionar mensagem
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      logger.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  }, [selectedFile, selectedConversation, sendingMessage]);

  const handleSendMedia = async (file: File) => {
    if (!selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedConversation);
      
      // Buscar messageId para responder (pode estar em external_id ou metadata)
      const replyMessageId = replyingToMessage?.external_id || 
                             replyingToMessage?.metadata?.messageId || 
                             replyingToMessage?.metadata?.external_id;
      if (replyMessageId) {
        formData.append('messageId', replyMessageId);
      }
      
      // Pegar legenda do campo via ref
      const caption = messageInputRef.current?.value?.trim();
      if (caption) {
        formData.append('content', caption);
      }
      
      // Se for um arquivo de áudio gravado
      if (file.name.includes('recording') || file.name.includes('voice')) {
        formData.append('is_voice', 'true');
      }

      const response = await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Sempre recarregar mensagens após envio bem-sucedido para garantir que apareçam
      if (messageInputRef.current) {
        messageInputRef.current.value = '';
      }
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);
    } catch (err: any) {
      logger.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para lidar com paste de imagens (Ctrl+V)
  // Campo de mensagem agora é uncontrolled - sem processamento durante digitação

  // Handler para tecla Enter
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
      e.preventDefault();
      const messageText = messageInputRef.current?.value?.trim();
      if (messageText || selectedFile) {
        handleSendMessage();
      }
    }
  }, [isRecording, selectedFile, handleSendMessage]);

  const handlePasteImage = useCallback((e: React.ClipboardEvent) => {
    if (!selectedConversation) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Verificar se é uma imagem
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (file) {
          // Criar um nome descritivo para o arquivo
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const renamedFile = new File(
            [file],
            `pasted-image-${timestamp}.png`,
            { type: file.type }
          );
          
          // Definir como arquivo selecionado (mostrará preview)
          setSelectedFile(renamedFile);
        }
        break;
      }
    }
  }, [selectedConversation]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tamanho (50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('Arquivo muito grande. Tamanho máximo: 50MB');
        return;
      }

      // Validar tipo (inclui documentos: PDF, Word, Excel, PPT, texto, ZIP)
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/mpeg', 'video/quicktime',
        'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/m4a',
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain', 'application/zip'
      ];
      const ext = (file.name.toLowerCase().split('.').pop() || '');
      const isPdf = ext === 'pdf' && (file.type === 'application/pdf' || !file.type);
      const isDoc = (ext === 'doc' || ext === 'docx') && (['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ''].includes(file.type));
      const isExcel = (ext === 'xls' || ext === 'xlsx') && (['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''].includes(file.type));
      const isPpt = (ext === 'ppt' || ext === 'pptx') && (['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ''].includes(file.type));
      const isTxt = ext === 'txt' && (file.type === 'text/plain' || !file.type);
      const isZip = ext === 'zip' && (file.type === 'application/zip' || !file.type);
      const hasValidType = allowedTypes.includes(file.type) || isPdf || isDoc || isExcel || isPpt || isTxt || isZip;
      if (!hasValidType) {
        setError('Tipo de arquivo não suportado');
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, { type: 'audio/webm' });
        setSelectedFile(audioFile);
        setAudioChunks([]);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioChunks(chunks);

      // Iniciar contador de tempo
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      logger.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (isRecording) {
      handleStopRecording();
    }
    setAudioChunks([]);
  };

  // Limpar intervalos ao desmontar
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder, isRecording]);

  // WebSocket para tempo real
  useEffect(() => {
    // Conectar ao WebSocket apenas uma vez
    if (!socketRef.current) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      socketRef.current = io(WS_URL, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        auth: {
          token: token
        }
      });

      socketRef.current.on('connect', () => {
        logger.websocket('connected', 'Telegram');
      });

      socketRef.current.on('new_message', (data) => {
        logger.websocket('new_message', {
          conversationId: data.message?.conversation_id || data.conversation_id,
          direction: data.message?.direction || data.direction,
        });
        
        // Padronizar o formato da mensagem
        let message = data.message || data;
        
        // Garantir que conversation_id está presente
        if (!message.conversation_id) {
          message.conversation_id = data.conversation_id || message.conversation_id;
        }
        
        // Garantir que attachments seja sempre um array (pode vir como string JSON do Supabase)
        if (message.attachments) {
          if (Array.isArray(message.attachments)) {
            // Já é um array, usar como está
            message = { ...message, attachments: message.attachments };
          } else if (typeof message.attachments === 'string') {
            try {
              // Tentar parsear como JSON
              message = { ...message, attachments: JSON.parse(message.attachments) };
            } catch (e) {
              logger.error('Erro ao parsear attachments como JSON:', e);
              message = { ...message, attachments: [] };
            }
          } else {
            // Tipo inesperado, usar array vazio
            message = { ...message, attachments: [] };
          }
        } else {
          message = { ...message, attachments: [] };
        }

        // Adicionar mensagem imediatamente se a conversa estiver selecionada
        // Comparar com selectedConversationRef para garantir que funciona mesmo se selectedConversation mudar
        const currentConversationId = selectedConversationRef.current || selectedConversation;
        if (message.conversation_id && message.conversation_id === currentConversationId) {
          logger.debug('Adicionando mensagem ao chat', {
            messageId: message.id,
            direction: message.direction,
            conversationId: message.conversation_id
          });
          
          setMessages(prev => {
            // Verificar se a mensagem já existe (evitar duplicatas)
            const exists = prev.some(msg => msg.id === message.id);
            if (exists) {
              console.log('⚠️ Mensagem já existe, ignorando duplicata:', message.id);
              return prev;
            }
            
            logger.debug(`Adicionando nova mensagem. Total antes: ${prev.length}`);
            const newMessages = [...prev, message];
            // Ordenar mensagens por data de envio (mais antigas primeiro)
            const sorted = newMessages.sort((a, b) => 
              new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
            );
            logger.debug(`Mensagens ordenadas. Total depois: ${sorted.length}`);
            
            // Forçar re-render para garantir que a mensagem apareça
            return sorted;
          });
          
          // Scroll para baixo quando nova mensagem chegar
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
        
        // Atualizar lista de conversas e reordenar por last_message_at
        setConversations(prev => {
          // Verificar se a conversa existe na lista atual
          const conversationExists = prev.some(conv => conv.id === message.conversation_id);
          
          logger.debug(`Nova mensagem recebida - Conversation ID: ${message.conversation_id}, Existe na lista: ${conversationExists}`);
          
          let updated = prev.map(conv => {
            if (conv.id === message.conversation_id) {
              // Verificar se a conversa está atualmente selecionada
              const isCurrentlySelected = conv.id === selectedConversationRef.current;
              
              return {
                ...conv,
                last_message_at: message.sent_at,
                last_message: message.content || conv.last_message,
                // Só incrementar unread_count se a conversa não estiver selecionada e for mensagem inbound
                unread_count: (message.direction === 'inbound' && !isCurrentlySelected) 
                  ? (conv.unread_count || 0) + 1 
                  : conv.unread_count
              };
            }
            return conv;
          });
          
          // Se a conversa não existe na lista, buscar ela do backend
          if (!conversationExists && message.conversation_id) {
            logger.debug(`Conversa não encontrada na lista, buscando do backend: ${message.conversation_id}`);
            
            // Buscar a conversa específica do backend com a última mensagem
            Promise.all([
              axios.get(`${API_BASE_URL}/conversations/${message.conversation_id}`),
              axios.get(`${API_BASE_URL}/messages/conversation/${message.conversation_id}?limit=1&sortOrder=desc`)
            ])
              .then(([convResponse, msgResponse]) => {
                console.log(`📥 Resposta da busca de conversa:`, {
                  conversationSuccess: convResponse.data.success,
                  messageSuccess: msgResponse.data.success,
                  conversationData: convResponse.data.data ? 'existe' : 'não existe',
                  messageData: msgResponse.data.data ? `${msgResponse.data.data.length} mensagens` : 'sem mensagens'
                });
                
                if (convResponse.data.success && convResponse.data.data) {
                  const newConversation = convResponse.data.data;
                  const lastMessage = msgResponse.data.success && msgResponse.data.data && msgResponse.data.data.length > 0
                    ? msgResponse.data.data[0]
                    : message;
                  
                  console.log(`✅ Adicionando conversa à lista:`, {
                    id: newConversation.id,
                    contactName: newConversation.contact?.name,
                    lastMessageAt: lastMessage.sent_at || message.sent_at
                  });
                  
                  setConversations(prevConvs => {
                    // Verificar se já não foi adicionada (evitar duplicatas)
                    if (prevConvs.some(c => c.id === newConversation.id)) {
                      console.log(`⚠️ Conversa já existe na lista, pulando adição`);
                      return prevConvs;
                    }
                    
                    // Adicionar a nova conversa e reordenar
                    const withNew = [...prevConvs, {
                      ...newConversation,
                      last_message_at: lastMessage.sent_at || message.sent_at,
                      last_message: lastMessage.content || message.content || newConversation.last_message,
                      unread_count: (message.direction === 'inbound' && newConversation.id !== selectedConversationRef.current) ? 1 : 0
                    }];
                    
                    const sorted = withNew.sort((a: any, b: any) => {
                      const dateA = new Date(a.last_message_at || a.updated_at || 0);
                      const dateB = new Date(b.last_message_at || b.updated_at || 0);
                      return dateB.getTime() - dateA.getTime();
                    });
                    
                    logger.debug(`Conversa adicionada e lista reordenada. Total: ${sorted.length}`);
                    return sorted;
                  });
                } else {
                  logger.error('Erro: Conversa não encontrada no backend ou resposta inválida');
                }
              })
              .catch(err => {
                logger.error('Erro ao buscar conversa:', err);
              });
          }
          
          // Reordenar todas as conversas por last_message_at (mais recentes primeiro)
          const sorted = updated.sort((a: any, b: any) => {
            const dateA = new Date(a.last_message_at || a.updated_at || 0);
            const dateB = new Date(b.last_message_at || b.updated_at || 0);
            return dateB.getTime() - dateA.getTime();
          });
          
          return sorted;
        });
      });

      socketRef.current.on('conversation_updated', () => {
        logger.websocket('conversation_updated');
        // Recarregar conversas para atualizar lista
        fetchConversations();
        // Atualizar mensagens se a conversa estiver selecionada
        if (selectedConversation) {
          fetchMessages(selectedConversation);
        }
      });

      socketRef.current.on('disconnect', () => {
        logger.warn('WebSocket desconectado');
      });

      socketRef.current.on('connect_error', (error) => {
        logger.error('Erro de conexão WebSocket:', error);
      });
    }

    // Entrar na sala da conversa quando selectedConversation mudar
    if (socketRef.current && selectedConversation) {
      socketRef.current.emit('join_conversation', selectedConversation);
    }

    return () => {
      // Não desconectar aqui, apenas sair da sala
      if (socketRef.current && selectedConversation) {
        socketRef.current.emit('leave_conversation', selectedConversation);
      }
    };
  }, [selectedConversation]); // Manter apenas selectedConversation como dependência

  // Carregar conversas iniciais
  useEffect(() => {
    fetchConversations();
  }, []);

  // Carregar mensagens quando uma conversa é selecionada
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      // Marcar conversa como lida quando selecionada
      markConversationAsRead(selectedConversation);
    }
  }, [selectedConversation, fetchMessages, markConversationAsRead]);

  // Scroll automático quando novas mensagens chegarem
  useEffect(() => {
    if (messages.length > 0 && selectedConversation) {
      // Scroll para baixo quando novas mensagens são adicionadas
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages.length, selectedConversation]); // Scroll quando número de mensagens mudar

  // Memoizar conversa selecionada para evitar buscas repetidas durante render
  const selectedConversationData = useMemo(() => {
    return conversations.find(c => c.id === selectedConversation);
  }, [conversations, selectedConversation]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  const handleContactClick = async (contact: Contact) => {
    setSelectedContact(contact);
    setEditingName(contact.name || '');
    setIsEditingName(false);
    setOpenLeadDetailsDialog(true);
    
    // Buscar estágios do CRM primeiro, depois o estágio atual do lead
    await fetchCrmStages();
    // Aguardar um pouco para garantir que os estágios foram carregados
    setTimeout(() => {
      fetchCurrentStage(contact.id);
    }, 200);
  };
  
  const fetchCrmStages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/stages`);
      if (response.data.success && response.data.data) {
        const stages = response.data.data
          .map((stage: any) => ({
            id: stage.id,
            name: stage.name,
            stage_order: stage.stage_order,
            color: stage.color || '#6B7280'
          }))
          .sort((a: any, b: any) => a.stage_order - b.stage_order);
        setCrmStages(stages);
      }
    } catch (err: any) {
      logger.error('Erro ao buscar estágios do CRM:', err);
    }
  };
  
  const fetchCurrentStage = async (contactId: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/crm/contacts/stages`, {
        contact_ids: [contactId]
      });
      if (response.data.success && response.data.data) {
        const stageOrder = response.data.data[contactId];
        if (stageOrder) {
          // Aguardar um pouco para garantir que os estágios foram carregados
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Buscar o ID do estágio pelo stage_order
          let stage = crmStages.find(s => s.stage_order === stageOrder);
          
          if (!stage && crmStages.length > 0) {
            // Se não encontrar, buscar novamente os estágios
            const updatedStagesResponse = await axios.get(`${API_BASE_URL}/crm/stages`);
            if (updatedStagesResponse.data.success && updatedStagesResponse.data.data) {
              const stages = updatedStagesResponse.data.data
                .map((s: any) => ({
                  id: s.id,
                  name: s.name,
                  stage_order: s.stage_order,
                  color: s.color || '#6B7280'
                }))
                .sort((a: any, b: any) => a.stage_order - b.stage_order);
              setCrmStages(stages);
              stage = stages.find((s: any) => s.stage_order === stageOrder);
            }
          }
          
          if (stage) {
            setCurrentStageId(stage.id);
          } else {
            setCurrentStageId(null);
          }
        } else {
          setCurrentStageId(null);
        }
      }
    } catch (err: any) {
      logger.error('Erro ao buscar estágio atual do lead:', err);
      setCurrentStageId(null);
    }
  };
  
  const handleStageChange = async (newStageId: string) => {
    if (!selectedContact || !newStageId || newStageId === currentStageId) {
      return;
    }

    setUpdatingStage(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/crm/contact/${selectedContact.id}/move-to-stage`, {
        stageId: newStageId
      });

      if (response.data.success) {
        setCurrentStageId(newStageId);
        // Atualizar o estágio na lista de conversas também
        const newStage = crmStages.find(s => s.id === newStageId);
        if (newStage) {
          setAllLeadsStages(prev => {
            const updated = new Map(prev);
            updated.set(selectedContact.id, newStage.stage_order);
            return updated;
          });
          
          // Atualizar stage4Leads se necessário
          if (newStage.stage_order === 4) {
            setStage4Leads(prev => new Set(prev).add(selectedContact.id));
          } else {
            setStage4Leads(prev => {
              const updated = new Set(prev);
              updated.delete(selectedContact.id);
              return updated;
            });
          }
        }
        logger.debug('Estágio atualizado com sucesso');
      } else {
        throw new Error(response.data.error || 'Erro ao atualizar estágio');
      }
    } catch (err: any) {
      logger.error('Erro ao atualizar estágio do lead:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao atualizar estágio do lead');
    } finally {
      setUpdatingStage(false);
    }
  };
  
  const handleDeleteMessage = async (message: Message) => {
    if (!selectedConversation) {
      setError('Conversa não selecionada');
      setMessageMenuAnchor(null);
      return;
    }

    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
    if (!contact) {
      setError('Contato não encontrado');
      setMessageMenuAnchor(null);
      return;
    }

    // Buscar messageId em múltiplos lugares
    const messageIdToDelete = message.external_id || 
                              message.metadata?.messageId || 
                              message.metadata?.external_id ||
                              null;

    setDeletingMessageId(message.id);
    try {
      const response = await axios.delete(`${API_BASE_URL}/messages/${message.id}`, {
        data: {
          messageId: messageIdToDelete,
          owner: message.direction === 'outbound'
        }
      });

      if (response.data.success) {
        // Remover mensagem do estado local
        setMessages(prev => prev.filter(m => m.id !== message.id));
        setMessageMenuAnchor(null);
      } else {
        throw new Error(response.data.error || 'Erro ao deletar mensagem');
      }
    } catch (err: any) {
      logger.error('Erro ao deletar mensagem:', err);
      const errorMessage = err.response?.data?.error || 'Erro ao deletar mensagem';
      setError(errorMessage);
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleCloseLeadDetails = () => {
    setOpenLeadDetailsDialog(false);
    setSelectedContact(null);
    setIsEditingName(false);
    setEditingName('');
  };

  const handleStartEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!selectedContact || !editingName.trim() || editingName === selectedContact.name) {
      setIsEditingName(false);
      return;
    }

    setUpdatingName(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/crm/leads/${selectedContact.id}`, {
        name: editingName.trim()
      });

      if (response.data.success) {
        // Atualizar o contato selecionado
        setSelectedContact({ ...selectedContact, name: editingName.trim() });
        
        // Atualizar na lista de conversas
        setConversations(prev => prev.map(conv => {
          if (conv.contact?.id === selectedContact.id) {
            return {
              ...conv,
              contact: { ...conv.contact, name: editingName.trim() }
            };
          }
          return conv;
        }));
        
        setIsEditingName(false);
      } else {
        throw new Error(response.data.error || 'Erro ao atualizar nome');
      }
    } catch (err: any) {
      logger.error('Erro ao atualizar nome do lead:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar nome do lead');
      // Reverter para o nome original em caso de erro
      setEditingName(selectedContact.name || '');
    } finally {
      setUpdatingName(false);
    }
  };

  // Função para identificar região do Brasil pelo prefixo do telefone
  const getRegionFromPhone = (phone?: string): string | null => {
    if (!phone) return null;
    
    // Remover caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Verificar se começa com 55 (código do Brasil)
    if (cleanPhone.startsWith('55')) {
      // Pegar os 2 dígitos após o 55 (DDD)
      const ddd = cleanPhone.substring(2, 4);
      
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    } else if (cleanPhone.length >= 2) {
      // Se não começa com 55, pode ser que o DDD já esteja no início
      const ddd = cleanPhone.substring(0, 2);
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    }
    
    return null;
  };

  // Funções para menu de agenda
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Função para enviar lead para a agenda
  const handleSendToAgenda = async (status: 'requested_call' | 'scheduled_waiting_date' | 'call_completed') => {
    handleMenuClose();
    
    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
    if (!contact) {
      showSnackbar('Contato não encontrado', 'error');
      return;
    }

    setSendingToAgenda(contact.id);

    try {
      const response = await axios.post(`${API_BASE_URL}/agenda/leads`, {
        contact_id: contact.id,
        conversation_id: selectedConversation,
        lead_name: contact.name || 'Lead sem nome',
        lead_email: contact.email || undefined,
        lead_phone: contact.phone || undefined,
        status: status,
        notes: status === 'requested_call' 
          ? 'Lead pediu agendamento da call via Telegram'
          : status === 'scheduled_waiting_date'
          ? 'Agendamento feito aguardando data'
          : 'Call exclusiva realizada',
        priority: 'normal',
        metadata: {
          source: 'telegram_inbox',
          created_from: 'telegram_chat',
          created_at: new Date().toISOString()
        }
      });

      const result = response.data;

      if (result.success) {
        const statusMessages = {
          requested_call: 'Lead enviado para agenda: Pediu agendamento da call',
          scheduled_waiting_date: 'Lead enviado para agenda: Agendamento feito aguardando data',
          call_completed: 'Lead enviado para agenda: Call exclusiva realizada'
        };
        showSnackbar(statusMessages[status], 'success');
      } else {
        if (result.message?.includes('Já existe')) {
          showSnackbar('Este lead já está na agenda com este status', 'warning');
        } else {
          showSnackbar(result.message || 'Erro ao enviar para agenda', 'error');
        }
      }
    } catch (error: any) {
      logger.error('Erro ao enviar para agenda:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response) {
        // Erro de resposta da API
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Erro ao enviar para agenda';
        if (error.response.status === 401) {
          showSnackbar('Sessão expirada. Por favor, faça login novamente.', 'error');
        } else if (error.response.status === 409) {
          showSnackbar('Este lead já está na agenda com este status', 'warning');
        } else {
          showSnackbar(errorMessage, 'error');
        }
      } else if (error.request) {
        // Erro de rede
        showSnackbar('Erro de conexão. Verifique sua internet.', 'error');
      } else {
        // Outro erro
        showSnackbar(error.message || 'Erro ao enviar para agenda', 'error');
      }
    } finally {
      setSendingToAgenda(null);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Função para verificar se o lead está no estágio 4 (Venda Realizada)
  const isLeadInStage4 = async (contactId: string): Promise<boolean> => {
    try {
      logger.debug('Checking stage for contact');
      // Usar a API /crm/all-leads que funciona corretamente
      const response = await axios.get(`${getApiUrl()}/crm/all-leads`);
      if (response.data.success && response.data.data) {
        const allLeads = response.data.data;
        console.log('📊 Total leads found:', allLeads.length);
        
        // Procurar o lead específico
        const targetLead = allLeads.find((lead: any) => lead.contacts?.id === contactId);
        
        if (targetLead) {
          console.log('🎯 Lead found in all-leads:', targetLead.contacts?.name);
          console.log('🎯 Stage order:', targetLead.crm_stages?.stage_order);
          const isStage4 = targetLead.crm_stages?.stage_order === 4;
          console.log('⭐ Is stage 4:', isStage4);
          return isStage4;
        } else {
          console.log('❌ Lead not found in all-leads');
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking lead stage:', error);
      return false;
    }
  };

  // Função para obter o estágio de qualquer lead
  const getLeadStage = (contactId: string): number | null => {
    return allLeadsStages.get(contactId) || null;
  };

  // Função para verificar estágios apenas dos contatos das conversas carregadas (otimizado)
  const checkAllLeadsStages = async (conversations: Conversation[]) => {
    console.log('🔍 checkAllLeadsStages called with conversations:', conversations.length);
    const stage4Set = new Set<string>();
    const stagesMap = new Map<string, number>();
    
    try {
      // Extrair apenas os contact_ids das conversas
      const contactIds = conversations
        .map(conv => conv.contact_id)
        .filter(Boolean) as string[];
      
      if (contactIds.length === 0) {
        console.log('⚠️ No contact IDs to check');
        setStage4Leads(stage4Set);
        setAllLeadsStages(stagesMap);
        return;
      }

      console.log(`📊 Buscando estágios para ${contactIds.length} contatos...`);
      
      // Buscar estágios apenas dos contatos das conversas carregadas (muito mais rápido)
      const response = await axios.post(`${getApiUrl()}/crm/contacts/stages`, {
        contact_ids: contactIds
      });
      
      if (response.data.success && response.data.data) {
        const stagesData = response.data.data; // { contact_id: stage_order }
        
        // Processar os estágios retornados
        Object.entries(stagesData).forEach(([contactId, stageOrder]) => {
          const stage = Number(stageOrder);
          if (stage) {
            stagesMap.set(contactId, stage);
            
            // Se for estágio 4, adicionar ao set
            if (stage === 4) {
              console.log('⭐ Adding to stage 4 set:', contactId);
              stage4Set.add(contactId);
            }
          }
        });
        
        console.log(`✅ Estágios encontrados para ${stagesMap.size} contatos`);
      }
    } catch (error) {
      console.error('Error fetching contact stages:', error);
    }
    
    console.log('🎯 Final stage 4 leads:', Array.from(stage4Set));
    console.log('🎯 All leads stages map:', Object.fromEntries(stagesMap));
    
    setStage4Leads(stage4Set);
    setAllLeadsStages(stagesMap);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showChatList, setShowChatList] = useState(true);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="calc(100vh - 80px)"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">Carregando conversas do Telegram...</Typography>
      </Box>
    );
  }

  // Função para voltar à lista no mobile
  const handleBackToList = () => {
    setShowChatList(true);
    if (isMobile) {
      setSelectedConversation('');
    }
  };

  return (
    <>
    <Box sx={{ 
      height: { xs: '100vh', md: 'calc(100vh - 80px)' },
      display: 'flex',
      flexDirection: 'column',
      bgcolor: { xs: '#f0f2f5', md: 'background.default' },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Mobile: Chat em tela cheia quando selecionado */}
      {isMobile && selectedConversation && !showChatList ? (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header do chat mobile */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            bgcolor: '#0088CC',
            color: '#000000',
            minHeight: '64px',
            paddingTop: 'max(16px, env(safe-area-inset-top))',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1
          }}>
            <IconButton 
              onClick={handleBackToList}
              sx={{ 
                mr: 2, 
                color: '#000000',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar 
              src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
              sx={{ 
                mr: 1.5, 
                bgcolor: conversations.find(c => c.id === selectedConversation)?.contact?.name 
                  ? getAvatarColor(conversations.find(c => c.id === selectedConversation)?.contact?.name || '') 
                  : '#3b82f6',
                width: 32,
                height: 32
              }}
            >
              {conversations.find(c => c.id === selectedConversation)?.contact?.name 
                ? conversations.find(c => c.id === selectedConversation)?.contact?.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                : <TelegramIcon />
              }
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TelegramIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  Telegram
                </Typography>
              </Box>
            </Box>
            
            {/* Botões de ação mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Atribuição de atendente */}
              {selectedConversation && (
                <Box sx={{ 
                  '& .MuiIconButton-root': {
                    color: '#000000',
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(0,0,0,0.08)',
                  }
                }}>
                  <AttendantAssignment conversationId={selectedConversation} />
                </Box>
              )}
              
              <IconButton
                size="small"
                sx={{ color: '#000000' }}
                onClick={() => {
                  const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                  if (contact) {
                    handleContactClick(contact);
                  }
                }}
              >
                <InfoIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#000000' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
                disabled={sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id}
              >
                {sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                  <CircularProgress size={16} sx={{ color: '#000000' }} />
                ) : (
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
          </Box>

          {/* Área de mensagens mobile */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflow: 'auto',
              bgcolor: '#E8F5E8'
            }}>
              {/* Mensagens do chat */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        setMessageMenuAnchor({ el: e.currentTarget as HTMLElement, messageId: message.id });
                      }}
                      sx={{
                        maxWidth: '80%',
                        bgcolor: message.direction === 'outbound' 
                          ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)'
                          : '#FFFFFF',
                        background: message.direction === 'outbound' 
                          ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)'
                          : undefined,
                        color: message.direction === 'outbound' ? '#0A1E0A' : '#475569',
                        fontWeight: message.direction === 'outbound' ? 500 : 400,
                        p: 2,
                        borderRadius: 3,
                        boxShadow: message.direction === 'outbound' 
                          ? '0 3px 8px rgba(0, 136, 204, 0.35), 0 1px 3px rgba(0, 0, 0, 0.2)'
                          : 1,
                        border: message.direction === 'outbound' ? '2px solid #8BC88B' : 'none',
                        cursor: 'pointer',
                        '&:active': {
                          transform: 'scale(0.98)',
                          transition: 'transform 0.1s'
                        }
                      }}
                    >
                      <Typography 
                        variant="body2"
                        sx={{
                          fontSize: '1rem', // 16px - fonte maior
                          lineHeight: 1.4,
                          fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", -apple-system, BlinkMacSystemFont, sans-serif',
                          fontWeight: message.direction === 'outbound' ? 500 : 400,
                          letterSpacing: 'normal',
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          mb: 0.5,
                          color: message.direction === 'outbound' ? '#0A1E0A' : '#475569',
                        }}
                      >
                        {message.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: message.direction === 'outbound' ? '#1A3A1A' : '#65676B',
                          fontSize: '0.6875rem', // 11px
                          display: 'block',
                          fontWeight: 500,
                          letterSpacing: '0.02em',
                          fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                      >
                        {formatTime(message.sent_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
              </Box>
            </Box>

            {/* Input de mensagem mobile */}
            <Box sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Digite sua mensagem..."
                  inputRef={messageInputRef}
                  size="small"
                  multiline
                  maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: '#ffffff',
                      border: 'none',
                      borderRadius: '20px',
                      boxShadow: 'none',
                      transition: 'none',
                      '&:hover': {
                        background: '#ffffff',
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused': {
                        boxShadow: 'none',
                        borderColor: 'transparent',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input, & textarea': {
                        color: '#000000',
                        '&::placeholder': {
                          color: '#94a3b8',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
                <IconButton 
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={sendingMessage}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Desktop: Layout normal */
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0, md: 3 }, 
          height: '100%',
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* Lista de Conversas */}
          <Paper className="glass-blue-card" sx={{ 
            flex: { xs: showChatList ? '1' : '0', md: '0 0 380px' },
            minWidth: { xs: 'auto', md: 380 },
            display: { xs: showChatList ? 'flex' : 'none', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 2 : 1, md: 1 },
            position: { xs: 'relative', md: 'static' },
            overflow: 'hidden',
            borderRadius: '12px', 
            flexDirection: 'column',
            minHeight: 0, // Importante para flex funcionar corretamente
          }}>
          <Box p={2.5} sx={{ 
            borderBottom: '1px solid rgba(59, 130, 246, 0.1)', 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                p: 0.75,
                borderRadius: '12px',
                bgcolor: 'rgba(37, 211, 102, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TelegramIcon sx={{ fontSize: 22, color: '#25D366' }} />
              </Box>
              <Typography variant="h6" sx={{ 
                color: '#000000', 
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em'
              }}>
                Conversas
              </Typography>
            </Box>
          </Box>
        
        {error && (
          <Box px={2.5} pt={2}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          </Box>
        )}
        
        {/* Campo de busca */}
        <Box px={2.5} pt={2} pb={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por nome, telefone ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                background: 'rgba(59, 130, 246, 0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                  background: 'rgba(59, 130, 246, 0.12)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  borderColor: 'rgba(59, 130, 246, 0.5)',
                },
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  color: '#475569',
                  '&::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                  },
                },
              },
            }}
          />
        </Box>
        
        {/* Toggle para mostrar/ocultar conversas arquivadas */}
        <Box px={2.5} pb={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#3b82f6',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#3b82f6',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#000000', fontSize: '0.875rem', fontWeight: 500 }}>
                Mostrar arquivadas
              </Typography>
            }
          />
        </Box>
        
        <List sx={{ 
          py: 1, 
          flex: 1, 
          overflow: 'auto',
          minHeight: 0, // Importante para flex funcionar corretamente
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              background: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}>
           {(() => {
             // Filtrar conversas baseado no termo de busca
             const filteredConversations = searchTerm.trim() === '' 
               ? conversations 
               : conversations.filter((conv) => {
                   const searchLower = searchTerm.toLowerCase();
                   const name = conv.contact?.name?.toLowerCase() || '';
                   const phone = conv.contact?.phone?.toLowerCase() || '';
                   const email = conv.contact?.email?.toLowerCase() || '';
                   return name.includes(searchLower) || phone.includes(searchLower) || email.includes(searchLower);
                 });
             
             if (filteredConversations.length === 0) {
               return (
                 <Box sx={{ p: 4, textAlign: 'center' }}>
                   <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                     {searchTerm.trim() ? 'Nenhuma conversa encontrada para sua busca' : 'Nenhuma conversa encontrada'}
                   </Typography>
                 </Box>
               );
             }
             
             return filteredConversations.map((conversation) => (
              <ListItem
                key={conversation.id}
                component="div"
                onClick={() => {
                  setSelectedConversation(conversation.id);
                  if (isMobile) {
                    setShowChatList(false);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  px: 2,
                  py: 1.75,
                  mx: 1,
                  my: 0.5,
                  borderRadius: '8px',
                  backgroundColor: selectedConversation === conversation.id 
                    ? 'rgba(59, 130, 246, 0.15)' 
                    : 'transparent',
                  borderLeft: selectedConversation === conversation.id ? 3 : 0,
                  borderColor: selectedConversation === conversation.id ? 'rgba(59, 130, 246, 0.8)' : 'transparent',
                  border: selectedConversation === conversation.id 
                    ? '1px solid rgba(59, 130, 246, 0.3)' 
                    : '1px solid transparent',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                  },
                  '&::before': selectedConversation === conversation.id ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: '60%',
                    backgroundColor: 'rgba(59, 130, 246, 0.8)',
                    borderRadius: '0 2px 2px 0',
                  } : {},
                }}
              >
                <ListItemAvatar sx={{ minWidth: 56 }}>
                  <Avatar 
                    src={conversation.contact?.avatar_url}
                    sx={{ 
                      bgcolor: conversation.contact?.name ? getAvatarColor(conversation.contact.name) : 'primary.main',
                      width: 48,
                      height: 48,
                      border: `2px solid ${mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      boxShadow: mode === 'dark' 
                        ? '0 2px 8px rgba(0,0,0,0.3)' 
                        : '0 2px 6px rgba(0,0,0,0.1)',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {conversation.contact?.name 
                      ? conversation.contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon />
                    }
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{ 
                    m: 0,
                    '& .MuiListItemText-primary': {
                      mb: 0.5,
                    },
                    '& .MuiListItemText-secondary': {
                      mt: 0,
                    }
                  }}
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box display="flex" alignItems="center" gap={0.75} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: selectedConversation === conversation.id ? 600 : 500,
                            fontSize: '0.95rem',
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transition: 'font-weight 0.2s',
                          }}
                        >
                          {conversation.contact?.name || 'Usuário'}
                        </Typography>
                        {/* Indicador de estágio do CRM - Apenas estágio 4 */}
                        {conversation.contact?.id && stage4Leads.has(conversation.contact.id) && (
                          <StarIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: '#FFD700',
                              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                              flexShrink: 0,
                            }} 
                          />
                        )}
                        {/* Badge de atendente atribuído */}
                        <AttendantAssignment 
                          conversationId={conversation.id} 
                          compact={true}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Botão de arquivar/desarquivar */}
                        <Tooltip title={conversation.status === 'archived' ? 'Desarquivar conversa' : 'Arquivar conversa'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (conversation.status === 'archived') {
                                handleUnarchiveConversation(conversation.id);
                              } else {
                                handleArchiveConversation(conversation.id);
                              }
                            }}
                            sx={{
                              color: conversation.status === 'archived' ? '#3b82f6' : '#3b82f6',
                              backgroundColor: conversation.status === 'archived' 
                                ? 'rgba(59, 130, 246, 0.1)' 
                                : 'transparent',
                              border: conversation.status === 'archived' 
                                ? '1px solid rgba(59, 130, 246, 0.3)' 
                                : 'none',
                              '&:hover': {
                                backgroundColor: conversation.status === 'archived' 
                                  ? 'rgba(59, 130, 246, 0.2)' 
                                  : 'rgba(59, 130, 246, 0.1)',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            {conversation.status === 'archived' ? (
                              <UnarchiveIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <ArchiveIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {conversation.unread_count > 0 && (
                          <Box
                            sx={{
                              backgroundColor: '#3b82f6',
                              color: '#ffffff',
                            borderRadius: '12px',
                            minWidth: 22,
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            px: 0.75,
                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.4)',
                            flexShrink: 0,
                          }}
                        >
                          {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                        </Box>
                        )}
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <TimeIcon 
                        sx={{ 
                          fontSize: 14, 
                          color: 'text.secondary',
                          opacity: 0.7 
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                          opacity: 0.8,
                          fontWeight: 400,
                        }}
                      >
                        {conversation.last_message_at ? formatTime(conversation.last_message_at) : ''}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
             ));
           })()}
          {/* Botão Carregar Mais */}
          {hasMoreConversations && (
            <Box sx={{ px: 2, py: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={loadMoreConversations}
                disabled={loadingMoreConversations}
                startIcon={loadingMoreConversations ? <CircularProgress size={18} /> : null}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.25,
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                  },
                }}
              >
                {loadingMoreConversations ? 'Carregando...' : 'Carregar mais conversas'}
              </Button>
            </Box>
          )}
        </List>
      </Paper>

          {/* Área de Mensagens */}
          <Box sx={{ 
            flex: { xs: showChatList ? '0' : '1', md: 1 },
            minWidth: 0,
            display: { xs: showChatList ? 'none' : 'flex', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 1 : 2, md: 1 },
            position: { xs: 'relative', md: 'static' },
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
        {selectedConversation ? (
          <>
            {/* Cabeçalho da Conversa */}
            <Paper className="glass-blue-card" sx={{ 
              p: 2, 
              borderRadius: '12px 12px 0 0', 
              borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
                  sx={{ 
                    bgcolor: (() => {
                      const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                      return contact?.name ? getAvatarColor(contact.name) : 'primary.main';
                    })(),
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    '& img': {
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                    }
                  }}
                >
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    return contact?.name 
                      ? contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon />;
                  })()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">
                      {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
                    </Typography>
                    <TelegramIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Telegram
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Atribuição de atendente */}
                  <AttendantAssignment conversationId={selectedConversation} />
                  
                  {/* Botão de controle do bot */}
                  <BotToggleButton 
                    conversationId={selectedConversation}
                    size="small"
                    onStatusChange={(enabled) => {
                      console.log(`Bot ${enabled ? 'enabled' : 'disabled'} for conversation ${selectedConversation}`);
                    }}
                  />
                  
                  {/* Ícone de estrela para leads do estágio 4 */}
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    console.log('🌟 Rendering star check - contact:', contact?.id, 'stage4Leads:', Array.from(stage4Leads), 'has contact:', stage4Leads.has(contact?.id || ''));
                    return contact && stage4Leads.has(contact.id) ? (
                      <Tooltip title="Lead no estágio 4 - Venda Realizada">
                        <IconButton
                          size="small"
                          sx={{ color: '#FFD700' }}
                        >
                          <StarIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null;
                  })()}
                  
                  <Tooltip title="Ver detalhes do lead">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          handleContactClick(contact);
                        }
                      }}
                    >
                      <PersonIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Atualizar avatar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          updateContactAvatar(contact.id);
                        }
                      }}
                      disabled={updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir lead">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          deleteContact(contact.id);
                        }
                      }}
                      disabled={deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  
                  {/* Menu de três pontinhos para agenda */}
                  <Tooltip title="Mais opções">
                    <IconButton
                      size="small"
                      onClick={handleMenuOpen}
                      disabled={sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <MoreVertIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>

            {/* Menu de opções da agenda */}
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 250,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  borderRadius: 2,
                }
              }}
            >
              <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Enviar para Agenda
              </Typography>
              <MenuItem 
                onClick={() => handleSendToAgenda('requested_call')}
                disabled={sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                sx={{ py: 1.5 }}
              >
                <Chip 
                  label="📞 Pediu Agendamento" 
                  size="small" 
                  color="primary"
                  sx={{ height: 20, fontSize: '0.7rem', mr: 1.5 }}
                />
                <Typography variant="body2">Pediu Agendamento da Call</Typography>
              </MenuItem>
              <MenuItem 
                onClick={() => handleSendToAgenda('scheduled_waiting_date')}
                disabled={sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                sx={{ py: 1.5 }}
              >
                <Chip 
                  label="⏰ Agendamento Feito" 
                  size="small" 
                  color="warning"
                  sx={{ height: 20, fontSize: '0.7rem', mr: 1.5 }}
                />
                <Typography variant="body2">Agendamento Feito Aguardando Data</Typography>
              </MenuItem>
              <MenuItem 
                onClick={() => handleSendToAgenda('call_completed')}
                disabled={sendingToAgenda === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                sx={{ py: 1.5 }}
              >
                <Chip 
                  label="✅ Call Realizada" 
                  size="small" 
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem', mr: 1.5 }}
                />
                <Typography variant="body2">Call Exclusiva Realizada</Typography>
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem 
                onClick={() => {
                  handleMenuClose();
                  const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                  if (contact) {
                    window.open(`/agenda?contactId=${contact.id}`, '_blank');
                  }
                }}
                sx={{ py: 1.5 }}
              >
                <CalendarTodayIcon sx={{ mr: 1.5, fontSize: 18, color: '#1976d2' }} />
                <Typography variant="body2">Ver na Agenda</Typography>
              </MenuItem>
            </Menu>

            {/* Área de Mensagens */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              bgcolor: '#E8F5E8',
              borderRadius: '0 0 12px 12px',
              position: 'relative',
            }}>
              {/* Header do chat */}
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(0, 136, 204, 0.2)',
                borderRadius: 3,
                border: '3px solid rgba(0, 136, 204, 0.4)',
                boxShadow: '0 2px 4px rgba(0, 136, 204, 0.3)',
                position: 'relative',
                zIndex: 1,
                mb: 2,
                flexShrink: 0 // Não encolher
              }}>
                <Typography variant="body2" color="#0D47A1" textAlign="center" sx={{ fontWeight: 700 }}>
                  📱 Chat do Telegram - {conversations.find(c => c.id === selectedConversation)?.contact?.name}
                </Typography>
              </Box>

              {/* Container com scroll interno */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                position: 'relative',
                zIndex: 1,
                minHeight: 0, // Importante para flex funcionar
                p: 3, // Padding igual ao WhatsApp
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                {messages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    mb: 1,
                    gap: 1
                  }}
                >
                  {/* Avatar para mensagens recebidas */}
                  {message.direction === 'inbound' && (
                    <Avatar 
                      src={selectedConversationData?.contact?.avatar_url}
                      sx={{ 
                        bgcolor: selectedConversationData?.contact?.name 
                          ? getAvatarColor(selectedConversationData.contact.name) 
                          : 'primary.main',
                        width: 32,
                        height: 32,
                        fontSize: '0.875rem'
                      }}
                    >
                      {selectedConversationData?.contact?.name 
                        ? selectedConversationData.contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                  )}
                  
                  <Paper
                    onClick={(e) => {
                      e.stopPropagation();
                      setMessageMenuAnchor({ el: e.currentTarget, messageId: message.id });
                    }}
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.direction === 'outbound' 
                        ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)' 
                        : '#FFFFFF',
                      background: message.direction === 'outbound' 
                        ? 'linear-gradient(135deg, #B8E6B8 0%, #A8D8A8 100%)' 
                        : undefined,
                      color: message.direction === 'outbound' ? '#0A1E0A' : '#475569',
                      fontWeight: message.direction === 'outbound' ? 500 : 400,
                      borderRadius: 3,
                      boxShadow: message.direction === 'outbound' 
                        ? '0 3px 8px rgba(0, 136, 204, 0.35), 0 1px 3px rgba(0, 0, 0, 0.2)'
                        : 1,
                      border: message.direction === 'outbound' ? '2px solid #8BC88B' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: message.direction === 'outbound' 
                          ? '0 4px 12px rgba(0, 136, 204, 0.4), 0 2px 4px rgba(0, 0, 0, 0.25)'
                          : '0 4px 8px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    {/* Renderizar anexos */}
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {message.attachments.map((attachment, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            {attachment.type === 'image' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                position: 'relative',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <Box sx={{ position: 'relative' }}>
                                  <img 
                                    src={normalizeAttachmentUrl(attachment.url)} 
                                    alt="Imagem"
                                    style={{ 
                                      width: 'auto', 
                                      maxWidth: '100%',
                                      height: 'auto',
                                      maxHeight: 250,
                                      objectFit: 'contain',
                                      display: 'block',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => setExpandedImage(normalizeAttachmentUrl(attachment.url))}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                  <Box 
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      zIndex: 10,
                                      display: 'flex',
                                      gap: 0.5
                                    }}
                                  >
                                    <Tooltip title="Clique para ampliar">
                                      <Box 
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          left: 8,
                                          zIndex: 10,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                                          color: '#fff',
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.5,
                                          fontSize: '0.75rem',
                                          pointerEvents: 'none'
                                        }}
                                      >
                                        <ZoomInIcon sx={{ fontSize: 16 }} />
                                        Ampliar
                                      </Box>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const link = document.createElement('a');
                                          link.href = normalizeAttachmentUrl(attachment.url);
                                          link.download = `image_${message.id}.jpg`;
                                          link.target = '_blank';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        sx={{ 
                                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                                          color: '#fff',
                                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }
                                        }}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                                {(attachment.width || attachment.height) && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1, color: 'text.secondary' }}>
                                    {attachment.width && attachment.height && `${attachment.width} × ${attachment.height}`}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {attachment.type === 'video' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                position: 'relative',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <video 
                                  controls
                                  style={{ 
                                    width: '100%', 
                                    height: 'auto',
                                    maxHeight: 400,
                                    objectFit: 'contain',
                                    display: 'block'
                                  }}
                                >
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="video/mp4" />
                                  Seu navegador não suporta vídeos.
                                </video>
                              </Box>
                            )}
                            {attachment.type === 'audio' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mp3" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/wav" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  {attachment.title && (
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      {attachment.title}
                                      {attachment.performer && ` - ${attachment.performer}`}
                                    </Typography>
                                  )}
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = attachment.title 
                                        ? `${attachment.title}.mp3` 
                                        : `audio_${message.id}.mp3`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'voice' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/webm" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ flex: 1 }}>
                                    🎤 Mensagem de voz
                                  </Typography>
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = `voice_${message.id}.ogg`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {(attachment.type === 'document' || attachment.type === 'pdf' || (attachment.file_name && attachment.file_name.toLowerCase().endsWith('.pdf'))) && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1.5,
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                              }}>
                                <Box sx={{ 
                                  fontSize: '2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 48,
                                  height: 48,
                                  borderRadius: 1,
                                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                  color: '#ef4444'
                                }}>
                                  📄
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
                                    {attachment.file_name || 'Documento PDF'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    PDF Document
                                  </Typography>
                                </Box>
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const url = normalizeAttachmentUrl(attachment.url);
                                    window.open(url, '_blank');
                                  }}
                                  sx={{
                                    color: '#3b82f6',
                                    '&:hover': {
                                      backgroundColor: 'rgba(59, 130, 246, 0.1)'
                                    }
                                  }}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Box>
                            )}
                            {attachment.type === 'document' && !attachment.file_name?.toLowerCase().endsWith('.pdf') && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Typography variant="body2">📎</Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {attachment.file_name || 'Documento'}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(normalizeAttachmentUrl(attachment.url), '_blank')}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Box>
                            )}
                            {attachment.type === 'sticker' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                display: 'inline-block'
                              }}>
                                <img 
                                  src={normalizeAttachmentUrl(attachment.url)} 
                                  alt="Sticker"
                                  style={{ 
                                    width: 120, 
                                    height: 120,
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Renderizar texto da mensagem - apenas se não for texto genérico de mídia ou se for caption real */}
                    {message.content && (
                      (() => {
                        const hasValidAttachments = message.attachments && Array.isArray(message.attachments) && message.attachments.length > 0;
                        const isGenericMediaText = message.content === '📷 Imagem' || message.content === '🎥 Vídeo' || message.content === '🎵 Áudio' || message.content === '🎤 Mensagem de voz';
                        
                        // Só mostrar texto se:
                        // 1. Não há attachments válidos, OU
                        // 2. Há attachments mas o texto não é genérico (é uma caption real)
                        if (hasValidAttachments && isGenericMediaText) {
                          return null; // Não mostrar texto genérico quando há mídia
                        }
                        
                        return (
                          <Typography 
                            sx={{
                              fontSize: '1rem', // 16px - fonte maior
                              lineHeight: 1.5,
                              fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", -apple-system, BlinkMacSystemFont, sans-serif',
                              fontWeight: message.direction === 'outbound' ? 500 : 400,
                              letterSpacing: '0.01em',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              color: 'inherit',
                            }}
                          >
                            {message.content}
                          </Typography>
                        );
                      })()
                    )}
                    
                    <Typography
                      variant="caption"
                      sx={{
                        color: message.direction === 'outbound' ? '#1A3A1A' : '#65676B',
                        fontSize: '0.6875rem', // 11px
                        display: 'block',
                        fontWeight: 500,
                        letterSpacing: '0.02em',
                        fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                        mt: 0.5,
                      }}
                    >
                      {formatTime(message.sent_at)}
                    </Typography>
                  </Paper>
                  <Menu
                    anchorEl={messageMenuAnchor?.messageId === message.id ? messageMenuAnchor.el : null}
                    open={messageMenuAnchor?.messageId === message.id || false}
                    onClose={() => {
                      setMessageMenuAnchor(null);
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setReplyingToMessage(message);
                        setMessageMenuAnchor(null);
                      }}
                    >
                      <EditIcon fontSize="small" sx={{ mr: 1 }} />
                      Responder
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleDeleteMessage(message);
                      }}
                      disabled={deletingMessageId === message.id}
                    >
                      {deletingMessageId === message.id ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      Deletar mensagem
                    </MenuItem>
                  </Menu>
                </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
                </Box>
              </Box>
            </Box>

            {/* Campo de Envio */}
            <Paper className="glass-blue-card" sx={{ 
              p: 2, 
              borderRadius: '0 0 12px 12px', 
              mt: 'auto',
            }}>
              {/* Preview de arquivo selecionado */}
              {selectedFile && !isRecording && (
                <Box sx={{ mb: 1, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ fontSize: '1.5rem' }}>
                    {selectedFile.type.startsWith('image/') && '📷'}
                    {selectedFile.type.startsWith('video/') && '🎥'}
                    {(selectedFile.type.startsWith('audio/') || selectedFile.name.includes('recording')) && '🎤'}
                    {(selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf')) && '📄'}
                    {!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/') && !selectedFile.type.startsWith('audio/') && selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith('.pdf') && '📎'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
                      {selectedFile.name}
                    </Typography>
                    {selectedFile.size > 0 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        {(selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf')) && ' • PDF Document'}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={handleCancelFile} sx={{ color: '#ef4444' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Indicador de gravação */}
              {isRecording && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'error.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                  <Typography variant="body2" sx={{ flex: 1, color: 'error.main', fontWeight: 500 }}>
                    Gravando... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: 'error.main' }}>
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="flex-end">
                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {/* Botão de anexar arquivo */}
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  <AttachFileIcon />
                </IconButton>

                {/* Botão de gravar áudio */}
                <IconButton
                  color={isRecording ? 'error' : 'primary'}
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={sendingMessage || !!selectedFile}
                  sx={{ flexShrink: 0 }}
                >
                  {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>

                {/* Botão de mensagens rápidas */}
                <QuickMessagesButton
                  onSelectMessage={(message) => {
                    if (messageInputRef.current) {
                      const input = messageInputRef.current as HTMLInputElement | HTMLTextAreaElement;
                      if (input) {
                        input.value = message;
                        // Disparar evento para atualizar o estado interno do Material-UI
                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                          window.HTMLInputElement.prototype,
                          'value'
                        )?.set;
                        if (nativeInputValueSetter) {
                          nativeInputValueSetter.call(input, message);
                        }
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                        messageInputRef.current?.focus();
                      }
                    }
                  }}
                  channel="telegram"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={selectedFile ? "Adicione uma legenda (opcional)..." : "Digite sua mensagem..."}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePasteImage}
                  inputRef={messageInputRef}
                  disabled={sendingMessage || isRecording}
                  multiline
                  maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: '#ffffff',
                      border: 'none',
                      borderRadius: '20px',
                      boxShadow: 'none',
                      transition: 'none',
                      '&:hover': {
                        background: '#ffffff',
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused': {
                        boxShadow: 'none',
                        borderColor: 'transparent',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input, & textarea': {
                        color: '#000000',
                        '&::placeholder': {
                          color: '#94a3b8',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Box>
            </Paper>
          </>
        ) : (
          <Paper className="glass-blue-card" sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', opacity: 0.7 }}>
              Selecione uma conversa para começar
            </Typography>
          </Paper>
        )}
          </Box>
        </Box>
      )}
      </Box>

    {/* Dialog para Detalhes do Lead */}
    <Dialog open={openLeadDetailsDialog} onClose={handleCloseLeadDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={selectedContact?.avatar_url || undefined}
              sx={{
                bgcolor: selectedContact?.avatar_url ? 'transparent' : '#3b82f6' + '15',
                color: '#3b82f6',
                fontWeight: 600,
                width: 48,
                height: 48,
                fontSize: '1.2rem'
              }}
            >
              {!selectedContact?.avatar_url && (selectedContact?.name || 'Lead').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              {isEditingName ? (
                <TextField
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  variant="standard"
                  fullWidth
                  autoFocus
                  disabled={updatingName}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      fontSize: '1.25rem', 
                      fontWeight: 600,
                      py: 0.5
                    }
                  }}
                />
              ) : (
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedContact?.name || 'Lead'}
              </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <TelegramIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                <Typography variant="body2" color="text.secondary">
                  Telegram Lead
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {isEditingName ? (
                <>
                  <IconButton 
                    size="small" 
                    onClick={handleSaveName}
                    disabled={updatingName || !editingName.trim() || editingName.trim() === selectedContact?.name}
                    color="primary"
                  >
                    {updatingName ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={handleCancelEditName}
                    disabled={updatingName}
                  >
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={handleStartEditName}
                  title="Editar nome"
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Informações Básicas */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Informações Básicas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Nome
                  </Typography>
                  {isEditingName ? (
                    <TextField
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={updatingName}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName();
                        } else if (e.key === 'Escape') {
                          handleCancelEditName();
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                    {selectedContact?.name || 'Não informado'}
                  </Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleStartEditName}
                        title="Editar nome"
                        sx={{ ml: 'auto' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.email || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.phone || 'Não informado'}
                  </Typography>
                  {selectedContact?.phone && getRegionFromPhone(selectedContact.phone) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                      📍 {getRegionFromPhone(selectedContact.phone)}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Canal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TelegramIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Telegram
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* IDs do Telegram */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                IDs do Telegram
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Telegram ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.telegram_id || 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Username
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.telegram_username ? `@${selectedContact.custom_fields.telegram_username}` : 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID Interno
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.id || 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Estágio do CRM */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Estágio do CRM
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="stage-select-label">Estágio</InputLabel>
                <Select
                  labelId="stage-select-label"
                  value={currentStageId || ''}
                  label="Estágio"
                  onChange={(e) => handleStageChange(e.target.value)}
                  disabled={updatingStage || crmStages.length === 0}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  {crmStages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: stage.color,
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body2">
                          {stage.stage_order}. {stage.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {updatingStage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Atualizando estágio...
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Box>

            {/* Campos Customizados */}
            {selectedContact?.custom_fields && Object.keys(selectedContact.custom_fields).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Campos Customizados
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {Object.entries(selectedContact.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {String(value) || 'Não disponível'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeadDetails}>Fechar</Button>
        </DialogActions>
    </Dialog>

    {/* Dialog para Imagem Ampliada */}
    <Dialog 
        open={!!expandedImage} 
        onClose={() => setExpandedImage(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 2,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Imagem Ampliada
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => {
                if (expandedImage) {
                  const link = document.createElement('a');
                  link.href = normalizeAttachmentUrl(expandedImage);
                  link.download = `image_${Date.now()}.jpg`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={() => setExpandedImage(null)}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          position: 'relative',
          minHeight: '60vh'
        }}>
          {expandedImage && (
            <img 
              src={normalizeAttachmentUrl(expandedImage || '')} 
              alt="Imagem ampliada"
              style={{ 
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                cursor: 'zoom-out'
              }}
              onClick={() => setExpandedImage(null)}
              onError={(e) => {
                // Silenciosamente tratar erro (arquivo pode ter expirado no Telegram)
                e.currentTarget.style.display = 'none';
                e.stopPropagation();
              }}
            />
          )}
        </DialogContent>
    </Dialog>

    {/* Snackbar para notificações */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Telegram;
